import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import CustomToast from '../../components/CustomToast';
import { apiErrorMessage, useCareersRequest } from './careersShared';
import { CareersPage, EmptyState, ErrorBanner, Loading, PageHeader } from './careersUI';
import './careers.css';

const NAME_MAX = 60;

const JobCategories = () => {
  const request = useCareersRequest();
  const navigate = useNavigate();
  const toastRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null); // { category_id, name }
  const [deleting, setDeleting] = useState(null);

  const notify = (severity, detail) => toastRef.current?.show(severity, severity === 'error' ? 'Error' : 'Done', detail);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const data = await request('/categories');
      setCategories(data.categories || []);
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Could not load categories.'));
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { load(); }, [load]);

  const run = async (action, successMessage) => {
    setBusy(true);
    try {
      await action();
      if (successMessage) notify('success', successMessage);
      await load();
      return true;
    } catch (error) {
      notify('error', apiErrorMessage(error));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    if (await run(() => request('/categories', { method: 'post', data: { name } }), `Added "${name}".`)) setNewName('');
  };

  const rename = async () => {
    const name = editing.name.trim();
    if (!name) return;
    if (await run(() => request(`/categories/${editing.category_id}`, { method: 'put', data: { name } }), 'Category renamed.')) {
      setEditing(null);
    }
  };

  const toggleActive = (category, isActive) => run(
    () => request(`/categories/${category.category_id}`, { method: 'put', data: { is_active: isActive } }),
    isActive ? `"${category.name}" is visible on the portal.` : `"${category.name}" is hidden from the portal.`,
  );

  const move = (index, delta) => {
    const ids = categories.map((c) => c.category_id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + delta, 0, id);
    // Show the new order right away; reload restores it if saving fails
    setCategories((prev) => ids.map((categoryId) => prev.find((c) => c.category_id === categoryId)));
    run(() => request('/categories/order', { method: 'put', data: { ids } }));
  };

  const confirmDelete = () => {
    const category = deleting;
    setDeleting(null);
    run(() => request(`/categories/${category.category_id}`, { method: 'delete' }), `Deleted "${category.name}".`);
  };

  return (
    <CareersPage narrow>
      <CustomToast ref={toastRef} />
      <PageHeader
        title="Job Categories"
        subtitle="Categories group jobs on the careers portal and appear there as filters, in this order. Hidden categories aren’t shown as filters and can’t be used for new jobs."
        back={{ label: 'Job Postings', onClick: () => navigate('/Graphe/dashboard/careers/jobs') }}
      />

      <div className="careers-card is-flush">
        <form className="careers-add-row" onSubmit={add}>
          <InputText
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name, e.g. Marketing"
            aria-label="New category name"
            maxLength={NAME_MAX}
            disabled={busy}
          />
          <Button type="submit" label="Add" icon="pi pi-plus" severity="help" disabled={busy || !newName.trim()} />
        </form>

        <ErrorBanner message={loadError} onRetry={load} />
        {loading ? (
          <Loading />
        ) : !categories.length && !loadError ? (
          <EmptyState icon="pi-tags" title="No categories yet">
            <p className="mb-0 small">Add one above to start posting jobs.</p>
          </EmptyState>
        ) : (
          <div className="careers-table-wrap">
            <table className="careers-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Order</th>
                  <th>Name</th>
                  <th style={{ width: 90 }}>Jobs</th>
                  <th style={{ width: 120 }}>On portal</th>
                  <th className="is-actions"><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category, index) => {
                  const isEditing = editing?.category_id === category.category_id;
                  return (
                    <tr key={category.category_id}>
                      <td className="is-num careers-order-buttons">
                        <Button
                          icon="pi pi-arrow-up"
                          text
                          rounded
                          severity="secondary"
                          size="small"
                          aria-label={`Move ${category.name} up`}
                          disabled={busy || index === 0}
                          onClick={() => move(index, -1)}
                        />
                        <Button
                          icon="pi pi-arrow-down"
                          text
                          rounded
                          severity="secondary"
                          size="small"
                          aria-label={`Move ${category.name} down`}
                          disabled={busy || index === categories.length - 1}
                          onClick={() => move(index, 1)}
                        />
                      </td>
                      <td>
                        {isEditing ? (
                          <form className="d-flex gap-1 align-items-center" onSubmit={(e) => { e.preventDefault(); rename(); }}>
                            <InputText
                              value={editing.name}
                              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                              aria-label={`Rename ${category.name}`}
                              maxLength={NAME_MAX}
                              autoFocus
                              className="flex-grow-1"
                            />
                            <Button type="submit" icon="pi pi-check" text rounded severity="success" aria-label="Save name" disabled={busy} />
                            <Button type="button" icon="pi pi-times" text rounded severity="secondary" aria-label="Cancel rename" onClick={() => setEditing(null)} />
                          </form>
                        ) : (
                          <span className={category.is_active ? 'careers-primary-text' : 'text-muted'}>{category.name}</span>
                        )}
                      </td>
                      <td className="is-num">{category.job_count}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <InputSwitch
                            checked={category.is_active}
                            onChange={(e) => toggleActive(category, e.value)}
                            disabled={busy}
                            aria-label={`Show ${category.name} on the portal`}
                          />
                          <small className="text-muted">{category.is_active ? 'Shown' : 'Hidden'}</small>
                        </div>
                      </td>
                      <td className="is-actions">
                        <div className="careers-row-actions">
                          <Button
                            icon="pi pi-pencil"
                            text
                            rounded
                            severity="secondary"
                            aria-label={`Rename ${category.name}`}
                            title="Rename"
                            disabled={busy || isEditing}
                            onClick={() => setEditing({ category_id: category.category_id, name: category.name })}
                          />
                          <Button
                            icon="pi pi-trash"
                            text
                            rounded
                            severity="danger"
                            aria-label={`Delete ${category.name}`}
                            title={category.job_count ? 'Used by jobs. Hide it instead.' : 'Delete'}
                            disabled={busy || category.job_count > 0}
                            onClick={() => setDeleting(category)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog
        header={`Delete "${deleting?.name}"?`}
        visible={Boolean(deleting)}
        style={{ width: '26rem', maxWidth: '95vw' }}
        className="careers-dialog"
        modal
        onHide={() => setDeleting(null)}
        footer={(
          <div>
            <Button label="Cancel" text severity="secondary" onClick={() => setDeleting(null)} />
            <Button label="Delete" severity="danger" onClick={confirmDelete} autoFocus />
          </div>
        )}
      >
        <p className="mb-0">This can’t be undone.</p>
      </Dialog>
    </CareersPage>
  );
};

export default JobCategories;
